import { useState, useEffect } from "react";
import { Calendar, Link2, Unlink, RefreshCw, CheckCircle, XCircle, Loader2, Download, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IntegrationsPage({ onLogout }) {
  const { lang, t } = useLanguage();
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: null, loading: true });
  const [syncing, setSyncing] = useState({ google: false });
  const [exportMonth, setExportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [exportType, setExportType] = useState("all");

  useEffect(() => {
    loadStatuses();
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_connected') === 'true') {
      toast.success(t("google_connected_success"));
      window.history.replaceState({}, document.title, window.location.pathname);
      loadStatuses();
    }
    if (urlParams.get('google_error')) {
      toast.error(`Google: ${urlParams.get('google_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadStatuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const googleRes = await axios.get(`${API}/google/status`, { headers }).catch(() => ({ data: { connected: false } }));
      setGoogleStatus({ ...googleRes.data, loading: false });
    } catch {
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  const connectGoogle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/google/auth-url`, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.authorization_url;
    } catch {
      toast.error(t("error"));
    }
  };

  const disconnectGoogle = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/google/disconnect`, { headers: { Authorization: `Bearer ${token}` } });
      setGoogleStatus({ connected: false, email: null, loading: false });
      toast.success(t("success"));
    } catch {
      toast.error(t("error"));
    }
  };

  const syncGoogleCalendar = async () => {
    setSyncing(s => ({ ...s, google: true }));
    try {
      const token = localStorage.getItem('token');
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axios.post(`${API}/google/sync-shifts?month=${currentMonth}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
    } catch {
      toast.error(t("error"));
    } finally {
      setSyncing(s => ({ ...s, google: false }));
    }
  };

  const downloadCalendarICS = () => {
    const token = localStorage.getItem('token');
    const url = `${API}/calendar/export/ics?month=${exportMonth}&type=${exportType}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => response.blob())
      .then(blob => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `athlo_nexus_${exportMonth}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(t("download_success"));
      })
      .catch(() => toast.error(t("download_error")));
  };

  const locale = lang === "pt" ? "pt-BR" : lang === "en" ? "en-US" : "de-DE";
  const monthOptions = [];
  const now = new Date();
  for (let i = -3; i <= 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
    monthOptions.push({ value, label });
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6 h-full overflow-y-auto" data-testid="integrations-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("integrations_title")}</h1>
          <p className="text-slate-600 text-sm mt-1">{t("integrations_subtitle")}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
          {/* Calendar Export */}
          <Card className="border-slate-200" data-testid="calendar-export-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileDown className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{t("calendar_export")}</CardTitle>
                  <CardDescription>{t("calendar_export_desc")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t("month")}</Label>
                <Select value={exportMonth} onValueChange={setExportMonth}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("data")}</Label>
                <Select value={exportType} onValueChange={setExportType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("all_data")}</SelectItem>
                    <SelectItem value="shifts">{t("only_shifts")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">{t("google_import_title")}</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>{t("google_import_1")}</li>
                  <li>{t("google_import_2")}</li>
                  <li>{t("google_import_3")}</li>
                  <li>{t("google_import_4")}</li>
                </ol>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={downloadCalendarICS} className="w-full" data-testid="download-ics-button">
                <Download className="w-4 h-4 mr-2" />{t("download_ics")}
              </Button>
            </CardFooter>
          </Card>

          {/* Google Calendar */}
          <Card className="border-slate-200" data-testid="google-calendar-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{t("google_calendar_api")}</CardTitle>
                  <CardDescription>{t("google_calendar_desc")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {googleStatus.loading ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /><span>{t("loading")}</span>
                </div>
              ) : googleStatus.connected ? (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <p className="font-medium">{t("connected")}</p>
                    <p className="text-sm text-green-700">{googleStatus.email}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-slate-500 bg-slate-50 px-3 py-2 rounded-lg">
                  <XCircle className="w-5 h-5" /><span>{t("not_connected")}</span>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex gap-2">
              {googleStatus.connected ? (
                <>
                  <Button variant="outline" onClick={syncGoogleCalendar} disabled={syncing.google} data-testid="sync-google-button">
                    {syncing.google ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {t("sync")}
                  </Button>
                  <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={disconnectGoogle} data-testid="disconnect-google-button">
                    <Unlink className="w-4 h-4 mr-2" />{t("disconnect")}
                  </Button>
                </>
              ) : (
                <Button onClick={connectGoogle} data-testid="connect-google-button">
                  <Link2 className="w-4 h-4 mr-2" />{t("connect_google")}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
